/**
 * @render react
 * @name ArticleGroup
*/

import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Date } from 'Components/atoms/Date';
import { Image } from 'Components/atoms/Image';
import { ArticleCard } from 'Components/molecules/ArticleCard';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * Article Group component
 * @param {Object} props - the props object
 * @param {string} props.title - title for the component
 * @param {string} props.titlePrefix - prefix for the title
 * @param {number} props.headingSize - size for the article group title
 * @param {string} props.ctaAriaLabel - CTA 'aria-label'
 * @param {string} props.ctaLabel - CTA label
 * @param {string} props.ctaUrl - CTA url
 * @param {string} props.ctaPosition - position of the CTA label. Supported positions: top and bottom. Default: top
 * @param {string} props.blankMessage - Message to display if no content
 * @param {boolean} props.showIndex - Display index numbers with each item in group
 * @param {string} props.image - object containing image src and alt
 * @param {string} props.sponsorText - additional text to go next to the sponsor logo image (ex "In association with [sponsor_image]")
 * @param {array} props.content - Array of content using the same keys as the component used for rendering
 * @param {component} props.component - Component to render the articles. For now, only ArticleCard component is supported
 * @param {Object} props.more - Object containing the url and label for the more link.
 * @param {Object} props.articleCardProps - Props to pass to articleCard
 * @param {number} props.hideImageMinimalIndex - As from which index images are hidden when articleCardStyle is minimal
 * @param {Object} props.authorDetails - object that contains author name (string) and image (src [string], alt [string])
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} props.extraClassNames - extra classes to be applied to the component
 * @param {Object} props.context - extra information coming from Polaris provider
 */
class ArticleGroup extends PureComponent {
  constructor(props) {
    super(props);

    this.componentsMap = {
      ArticleCard,
    };
  }

  render() {
    const {
      articleCardProps,
      articleCardStyle,
      articlesPerRow,
      authorDetails,
      blankMessage,
      component,
      content,
      context: {
        cssPrefix,
      },
      ctaAriaLabel,
      ctaLabel,
      ctaPosition,
      ctaUrl,
      date,
      dateFormat,
      extraClassNames,
      headingSize,
      hideImageMinimalIndex,
      image,
      loadStyles,
      more,
      showIndex,
      sponsorText,
      title,
      titlePrefix,
    } = this.props;

    // dynamically set the JSX tag name by looking in our map
    const TagName = this.componentsMap[component || 'ArticleCard'];

    const cta = (
      <div className={`${cssPrefix}__article-group--cta`}>
        <Link href={ctaUrl} ariaLabel={ctaAriaLabel}>{ctaLabel}</Link>
      </div>
    );

    return (
      <div
        className={classNames({
          [`${cssPrefix}__article-group`]: loadStyles,
          [`${cssPrefix}__article-group__row-${articlesPerRow}`]: loadStyles,
          [`-${articleCardStyle}`]: true,
          '-with-cta': ctaUrl,
          [`-with-cta-${ctaPosition}`]: ctaUrl,
          '-with-image': image && image.src,
          ...extraClassNames,
        })}
      >
        {title && (
          <div className={`${cssPrefix}__article-group--header`}>
            <div className={`${cssPrefix}__article-group--block`}>
              {title && (
                <Heading
                  size={headingSize}
                  extraClassNames={{ [`${cssPrefix}__article-group--title`]: true }}
                >
                  {titlePrefix && (
                    <span className={`${cssPrefix}__article-group--title-prefix`}>{titlePrefix}</span>
                  )}
                  {title}
                </Heading>
              )}

              {date && (
                <Date date={date} format={dateFormat} extraClassNames={{ [`${cssPrefix}__article-group--date`]: true }} />
              )}

              {image && image.src && (
                <div className={`${cssPrefix}__article-group--image`}>
                  {sponsorText && <span>{sponsorText}</span>}
                  <Image {...image} size="sponsor" />
                </div>
              )}

              {authorDetails && (
                <div className={`${cssPrefix}__article-group--author-details`}>
                  {authorDetails.image && <Image {...authorDetails.image} size="author-small" />}
                  {authorDetails.name && <span className={`${cssPrefix}__article-group--author-name`}>{authorDetails.name}</span>}
                </div>
              )}
            </div>

            {ctaUrl && (ctaPosition === 'top' || ctaPosition === 'both') && (
              { ...cta }
            )}
          </div>
        )}
        <div className={`${cssPrefix}__article-group--articles`}>
          {
            content.length === 0 && blankMessage ? (
              <div className={`${cssPrefix}__article-group--blank`}>
                {blankMessage}
              </div>
            ) : (
              content.map((article, index) => {
                const articleData = { ...article };
                articleData.extraClassNames = {
                  ...articleData.extraClassNames,
                  [`${cssPrefix}__article-group--single`]: true,
                  [`-${articleCardStyle}`]: true,
                };
                articleData.layout = articleCardStyle;

                // Featured layout. Only the first item is featured.
                if (index === 0 && articleCardStyle === 'featured') {
                  articleData.layout = 'featured';
                  articleData.datePosition = 'meta';
                } else if (articleCardStyle === 'minimal') {
                  // no excerpt
                  articleData.excerpt = null;

                  if (index > hideImageMinimalIndex) {
                    articleData.image = null;
                  }
                } else if (articleCardStyle === 'side') {
                  articleData.showRating = true;
                } else if (articleCardStyle === 'enhanced-index-block') {
                  // no excerpt
                  articleData.excerpt = null;
                  // imageSize is set to 'hero' for the first article
                  articleData.imageSize = index === 0 ? 'hero' : 'card';
                  articleData.isHeading = false;
                  articleData.showRating = true;
                }

                articleData.showIndex = showIndex;

                if (articleData.isSponsored) {
                  articleData.type = 'sponsored';
                }

                if (articleData.mediaType === 'VIDEO') {
                  articleData.extraClassNames['-video-icon'] = true;
                }

                if (article.component) {
                  return (<Fragment key={index}>{article.component}</Fragment>);
                }
                return (
                  <TagName {...articleData} {...articleCardProps} key={index} articleIndex={(index + 1)} />
                );
              })
            )
          }
        </div>

        {
          (more.url && more.label)
          && (
            <div className={`${cssPrefix}__article-group--more`}>
              <Link href={more.url}>{more.label}</Link>
            </div>
          )
        }

        {ctaUrl && (ctaPosition === 'bottom' || ctaPosition === 'both') && (
          { ...cta }
        )}
      </div>
    );
  }
}

ArticleGroup.propTypes = {
  articleCardProps: PropTypes.shape(),
  articleCardStyle: PropTypes.string,
  articlesPerRow: PropTypes.number,
  authorDetails: PropTypes.shape({
    image: PropTypes.shape({
      alt: PropTypes.string,
      src: PropTypes.string,
    }),
    name: PropTypes.string,
  }),
  blankMessage: PropTypes.node,
  component: PropTypes.string,
  content: PropTypes.arrayOf(PropTypes.shape()),
  context: PropTypes.shape(),
  ctaAriaLabel: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaPosition: PropTypes.oneOf(['top', 'bottom', 'both']),
  ctaUrl: PropTypes.string,
  date: PropTypes.string,
  dateFormat: PropTypes.string,
  extraClassNames: PropTypes.shape(),
  headingSize: PropTypes.number,
  hideImageMinimalIndex: PropTypes.number,
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    src: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  loadStyles: PropTypes.bool,
  more: PropTypes.objectOf(PropTypes.string),
  showIndex: PropTypes.bool,
  sponsorText: PropTypes.string,
  title: PropTypes.string,
  titlePrefix: PropTypes.string,
};

ArticleGroup.defaultProps = {
  articleCardProps: {},
  articleCardStyle: 'default', // values can be default, featured, minimal, side, cover
  articlesPerRow: 3,
  authorDetails: null,
  blankMessage: null,
  component: 'ArticleCard',
  content: [],
  context: {},
  ctaAriaLabel: '',
  ctaLabel: '',
  ctaPosition: 'top',
  ctaUrl: '',
  date: '',
  dateFormat: 'D MMMM YYYY',
  extraClassNames: {},
  headingSize: 4,
  hideImageMinimalIndex: 3,
  image: {
    alt: '',
    height: 0,
    src: '',
    width: 0,
  },
  loadStyles: true,
  more: {},
  showIndex: false,
  sponsorText: '',
  title: null,
  titlePrefix: '',
};

export default withPolaris(ArticleGroup);
